import PropTypes from 'prop-types';
import { useState } from 'react';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton,Typography, MenuItem, Menu, ButtonBase, ListItemIcon, ListItemText } from '@mui/material';
// components
import Iconify from '../../components/Iconify';
import { SITE_TYPE_OPTIONS, getSiteTypeOption, isSiteTypeEnabled, setSiteType } from '../../utils/siteType';
import useSiteType from '../../hooks/useSiteType';
//
// import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
// import NotificationsPopover from './NotificationsPopover';


// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 60;

// The sidebar is a persistent drawer that is always in flow, so the fixed AppBar
// has to start where the drawer ends. --sidebar-width is kept up to date by the
// layout on collapse/expand; the fallback covers the first paint before the
// layout effect runs.
const SIDEBAR_WIDTH = 'var(--sidebar-width, 280px)';

const RootStyle = styled(AppBar)(({ theme }) => ({
  position: 'fixed',
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  width: `calc(100% - ${SIDEBAR_WIDTH})`,
  marginLeft: SIDEBAR_WIDTH,
  // matches the drawer's own width transition so the two move together
  transition: theme.transitions.create(['width', 'margin-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));


// Reads as part of the bar rather than a form control: no outline or label,
// just the active site type with its icon, opening a menu on click.
const SiteTypeButton = styled(ButtonBase)(({ theme }) => ({
  ...theme.typography.subtitle2,
  height: 36,
  paddingInline: theme.spacing(1.25),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  color: theme.palette.text.primary,
  gap: theme.spacing(0.75),
  transition: theme.transitions.create(['background-color', 'color']),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
  '&[aria-expanded="true"]': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  },
}));

// ----------------------------------------------------------------------

export default function DashboardNavbar({ onOpenSidebar, setToken, setUserType, userData }) {
  // Persisted so the choice survives navigation. Pages read it with the same
  // useSiteType() hook, so selecting here refetches them.
  const siteType = useSiteType();
  const [siteTypeAnchor, setSiteTypeAnchor] = useState(null);
  const showSiteType = isSiteTypeEnabled(userData?.client_id);
  const activeSiteType = getSiteTypeOption(siteType);

  const handleSiteTypeSelect = (value) => {
    setSiteType(value);
    setSiteTypeAnchor(null);
  };

  return (
    <RootStyle>

      <ToolbarStyle>
        {/* <Typography variant="h4" color="primary" sx={{ ml: 18 }}>
          {resolvedTitle}
        </Typography> */}
        <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary', display: { lg: 'none' } }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>

        {showSiteType && (
          <>
            <SiteTypeButton
              onClick={(event) => setSiteTypeAnchor(event.currentTarget)}
              aria-expanded={Boolean(siteTypeAnchor)}
              aria-haspopup="menu"
              aria-label={`Site type: ${activeSiteType.label}`}
            >
              <Iconify icon={activeSiteType.icon} width={20} height={20} />
              {activeSiteType.label}
              <Iconify
                icon="eva:chevron-down-fill"
                width={18}
                height={18}
                sx={{
                  opacity: 0.6,
                  transition: (theme) => theme.transitions.create('transform'),
                  transform: siteTypeAnchor ? 'rotate(180deg)' : 'none',
                }}
              />
            </SiteTypeButton>

            <Menu
              open={Boolean(siteTypeAnchor)}
              anchorEl={siteTypeAnchor}
              onClose={() => setSiteTypeAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 215, borderRadius: 1.5 } }}
            >
              {SITE_TYPE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === activeSiteType.value}
                  onClick={() => handleSiteTypeSelect(option.value)}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Iconify icon={option.icon} width={20} height={20} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{option.label}</ListItemText>
                  {option.value === activeSiteType.value && (
                    <Iconify icon="eva:checkmark-fill" width={18} height={18} sx={{ ml: 1, color: 'primary.main' }} />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* <Searchbar /> */}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* <LanguagePopover /> */}
          {/* <NotificationsPopover /> */}
          <AccountPopover setToken={setToken} setUserType={setUserType}/>
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
  userData: PropTypes.object,
  setToken: PropTypes.func.isRequired,
  // setUserType: PropTypes.func.isRequired
}
